import { type Static, type StaticDecode, type TSchema, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { Nullable, ReadonlyObject } from "@/shared/schema/schema.ts";
import { Role } from "@/shared/schema/user.ts";
import { type PassedValidator, decoded } from "@/shared/util/decoded.ts";
import { invariant } from "@/shared/util/invariant.ts";

// Must match the monorepo shells' BOOTSTRAP_ELEMENT_ID in rspack/shells.mjs.
const BOOTSTRAP_ELEMENT_ID = "cvp-bootstrap";
const DEPLOY_CONFIG_ELEMENT_ID = "cvp-deploy-config";

const bootstrapSchema = ReadonlyObject({
  logged_in: Type.Boolean(),
  locale: Type.String(),
  default_locale: Type.String(),
  csrf_token: Type.String(),
  required_action: Nullable(
    Type.Union([
      Type.Literal("reset_password"),
      Type.Literal("select_office"),
      Type.Literal("privacy_notice"),
    ]),
  ),
  disable_timeout_and_compatibility_checks: Type.Boolean(),
  current_user_id: Type.Optional(Type.String()),
  current_user_roles: Type.Optional(Type.Array(Role)),
  // Both are sent only for a signed-in user. A CVP admin, not a company role,
  // which is why admin is not one of current_user_roles.
  admin: Type.Optional(Type.Boolean()),
  // Narrower still: only for an admin, and true until the session passes OTP.
  otp_pending: Type.Optional(Type.Boolean()),
  pusher_key: Type.String(),
  pusher_cluster: Type.String(),
  unleash_context: ReadonlyObject({
    userId: Type.String(),
    properties: ReadonlyObject({
      subdomain: Type.String(),
      companyId: Type.String(),
      cvpAdmin: Type.String(),
      companyCreatedAt: Type.Integer(),
    }),
  }),
  minimum_compatible_version: Type.Integer(),
  // Absent when Intercom is not configured for the environment, which is the
  // signal not to boot it. user_hash is keyed with the app secret, so only the
  // server can produce it.
  intercom: Type.Optional(
    ReadonlyObject({
      app_id: Type.String(),
      api_base: Type.Optional(Type.String()),
      user_hash: Type.Optional(Type.String()),
    }),
  ),
});

const deployConfigSchema = ReadonlyObject({
  sentryDsn: Type.Optional(Type.String()),
  environment: Type.Optional(Type.String()),
  release: Type.Optional(Type.String()),
  unleashClientKey: Type.Optional(Type.String()),
  awsRegion: Type.Optional(Type.String()),
});

// Read before the deploy config is validated, so that validation has a Sentry
// client to report to.
const sentryConfigSchema = Type.Pick(deployConfigSchema, [
  "sentryDsn",
  "environment",
  "release",
]);

export const bootstrapChecker = TypeCompiler.Compile(bootstrapSchema);
const deployConfigChecker = TypeCompiler.Compile(deployConfigSchema);
const sentryConfigChecker = TypeCompiler.Compile(sentryConfigSchema);

export type BootstrapPayload = Static<typeof bootstrapSchema>;
type DeployConfig = Static<typeof deployConfigSchema>;
export type SentryConfig = Static<typeof sentryConfigSchema>;

function readElement(id: string): unknown {
  const element = document.getElementById(id);

  return element?.textContent
    ? (JSON.parse(element.textContent) as unknown)
    : undefined;
}

// For elements the page cannot boot without. Throwing here renders the error
// page rather than leaving a blank one.
export function readJsonScript<T extends TSchema>(
  id: string,
  checker: PassedValidator<T>,
): StaticDecode<T> {
  const raw = readElement(id);

  invariant(
    raw !== undefined,
    `Missing #${id}. It must be rendered before the bundle's script tag.`,
  );

  return checker.Decode(raw);
}

// The page has moved on from the URL it booted at, so its payload describes
// another page, and the thrower is reloading it. The boundaries render nothing
// for this and leave the reporting to the thrower.
export class StalePayloadError extends Error {
  name = "StalePayloadError";
}

let bootstrap: BootstrapPayload | undefined;

export function getBootstrap(): BootstrapPayload {
  bootstrap ??= readJsonScript(BOOTSTRAP_ELEMENT_ID, bootstrapChecker);

  return bootstrap;
}

// On a statically served page the inlined token belongs to the session Rails
// minted for nginx's subrequest, which the visitor never received, so the login
// pack fetches one of its own. Replaced inside the payload rather than kept
// alongside it: one token, one place to read it.
export function setCsrfToken(csrf_token: string) {
  bootstrap = { ...getBootstrap(), csrf_token };
}

// An absent element means no Sentry, not a boot failure.
export function getDeployConfig(): DeployConfig {
  const raw = readElement(DEPLOY_CONFIG_ELEMENT_ID);

  return raw === undefined ? {} : decoded(deployConfigChecker, raw);
}

export function readSentryConfig(): SentryConfig {
  const raw = readElement(DEPLOY_CONFIG_ELEMENT_ID);

  return sentryConfigChecker.Check(raw) ? raw : {};
}
